import { ReactNode } from 'react';
import { BasePage } from '@/components/ui/base-page';
import { BreadcrumbItem } from '@/components/ui/breadcrumb-nav';
import { Outlet } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/card';
import { cn } from '@/lib/utils';

interface ServicePageProps {
    title: string;
    description: string;
    category?: string;
    icon?: ReactNode;
    breadcrumbs?: BreadcrumbItem[];
    heroImage?: string;
    accentColor?: string;
    /** "partner" = premium card-style hero for expansion/M&A/advisory; "default" = classic full-bleed hero */
    variant?: 'default' | 'partner';
    children: ReactNode;
}

export function ServicePage({
    title,
    description,
    category,
    icon,
    breadcrumbs,
    heroImage,
    accentColor = "#0f172a",
    variant = 'default',
    children
}: ServicePageProps) {
    const isPartner = variant === 'partner';

    return (
        <BasePage breadcrumbs={breadcrumbs}>
            {/* Hero: partner variant = card-style premium; default = full-bleed */}
            {isPartner ? (
                <div className="container mb-10">
                    <Card className="overflow-hidden border border-border/80 bg-gradient-to-br from-card to-muted/30 shadow-sm">
                        <div className="relative border-l-4 border-primary">
                            <CardContent className="pt-8 pb-8 pl-8 pr-8 md:pt-10 md:pb-10 md:pl-10 md:pr-10">
                                {category && (
                                    <span className="inline-flex items-center rounded-full border border-primary/30 bg-primary/5 px-3 py-1 text-xs font-medium uppercase tracking-wide text-primary">
                                        {category}
                                    </span>
                                )}
                                {icon && <div className="mt-4 mb-2">{icon}</div>}
                                <h1 className={cn(
                                    "mt-3 text-2xl font-semibold tracking-tight md:text-3xl",
                                    "text-foreground"
                                )}>
                                    {title}
                                </h1>
                                <p className="mt-3 max-w-2xl text-base text-muted-foreground md:text-lg">
                                    {description}
                                </p>
                            </CardContent>
                        </div>
                    </Card>
                </div>
            ) : (
                <div
                    className={`relative ${heroImage ? 'py-20' : 'py-12'} mb-8`}
                    style={{
                        backgroundImage: heroImage ? `url(${heroImage})` : undefined,
                        backgroundColor: !heroImage ? accentColor : undefined
                    }}
                >
                    {heroImage && <div className="absolute inset-0 bg-black/50" />}

                    <div className="container relative z-10">
                        {category && (
                            <div className={`text-sm font-medium mb-2 ${heroImage ? 'text-white/80' : 'text-primary'}`}>
                                {category}
                            </div>
                        )}

                        {icon && <div className="mb-4">{icon}</div>}

                        <h1 className={`text-3xl md:text-4xl font-bold mb-4 ${heroImage ? 'text-white' : ''}`}>
                            {title}
                        </h1>

                        <p className={`text-lg md:text-xl max-w-2xl ${heroImage ? 'text-white/90' : 'text-muted-foreground'}`}>
                            {description}
                        </p>
                    </div>
                </div>
            )}

            {/* Main content */}
            <div className="container pb-16">
                {children}

                <Outlet />
            </div>
        </BasePage>
    );
} 